import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import type { Booking } from '../types/availability';

interface UserProfile {
  id: string;
  name: string;
  imageUrl?: string;
  email?: string;
}

interface BookingWithProfile extends Booking {
  participantProfile?: UserProfile;
  userRole: 'customer' | 'star';
}

const MyBookingsScreen: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'upcoming' | 'past'>('upcoming');
  const [earlyJoinModal, setEarlyJoinModal] = useState<{ show: boolean; minutesLeft: number }>({ show: false, minutesLeft: 0 });

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          id: userId,
          name: userData.name || userData.displayName || userData.email || 'Unknown User',
          imageUrl: userData.imageUrl || userData.profileImageUrl,
          email: userData.email
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const handleProfileClick = (participantId: string) => {
    navigate(`/star/${participantId}`);
  };

  useEffect(() => {
    if (!user) return;
    
    const fetchBookings = async () => {
      if (!user) {
        console.log('No user found');
        return;
      }
      
      try {
        console.log('Fetching bookings for user:', user.uid);
        const now = new Date();
        
        // Fetch bookings where user is either customer or star
        const customerQuery = query(
          collection(db, 'bookings'),
          where('customerId', '==', user.uid),
          orderBy('startTime', 'desc')
        );
        
        const starQuery = query(
          collection(db, 'bookings'),
          where('starId', '==', user.uid),
          orderBy('startTime', 'desc')
        );

        const [customerSnapshot, starSnapshot] = await Promise.all([
          getDocs(customerQuery),
          getDocs(starQuery)
        ]);

        // Use a Map to deduplicate bookings by ID
        const bookingsMap = new Map<string, Booking>();
        
        // Process customer bookings (user is the customer)
        customerSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const booking = {
            ...data,
            id: doc.id, // Use Firestore document ID
            startTime: data.startTime.toDate(),
            endTime: data.endTime.toDate(),
            createdAt: data.createdAt.toDate()
          } as Booking;
          bookingsMap.set(doc.id, booking);
        });
        
        // Process star bookings (user is the star)
        starSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const booking = {
            ...data,
            id: doc.id, // Use Firestore document ID
            startTime: data.startTime.toDate(),
            endTime: data.endTime.toDate(),
            createdAt: data.createdAt.toDate()
          } as Booking;
          // Only add if not already in map (prevents duplicates)
          if (!bookingsMap.has(doc.id)) {
            bookingsMap.set(doc.id, booking);
          }
        });

        // Convert map to array
        const allBookings = Array.from(bookingsMap.values());

        console.log('Processed bookings:', allBookings);

        // Filter based on selected filter
        const filteredBookings = allBookings.filter(booking => {
          if (filter === 'upcoming') return booking.startTime >= now;
          if (filter === 'past') return booking.startTime < now;
          return true;
        });

        // Sort: upcoming = ascending (closest first), past = descending (most recent first)
        if (filter === 'upcoming') {
          filteredBookings.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        } else {
          filteredBookings.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());
        }

        // Fetch participant profiles with correct logic
        const bookingsWithProfiles: any[] = await Promise.all(
          filteredBookings.map(async (booking) => {
            let participantId: string;
            let userRole: 'customer' | 'star';
            
            // Determine who the other participant is based on current user's role
            if (booking.customerId === user.uid || booking.fanId === user.uid) {
              // Current user is the customer/fan, so show star's profile
              participantId = booking.starId;
              userRole = 'customer';
            } else {
              // Current user is the star, so show customer's profile
              participantId = booking.customerId || booking.fanId;
              userRole = 'star';
            }
            
            const participantProfile = participantId ? await fetchUserProfile(participantId) : null;

            // Fallback: use names stored on the booking document if profile fetch fails
            const fallbackProfile = !participantProfile ? {
              id: participantId,
              name: userRole === 'star'
                ? booking.customerName || 'Unknown'
                : booking.starName || 'Unknown',
            } as UserProfile : null;

            return {
              ...booking,
              participantProfile: participantProfile || fallbackProfile,
              userRole
            };
          })
        );

        setBookings(bookingsWithProfiles);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user, filter]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">My Bookings</h1>
        
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 sm:mb-8">
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filter === 'upcoming'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
              filter === 'past'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Past
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-500">No bookings found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map(booking => (
              <div
                key={booking.id}
                className="bg-white rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1">
                    {/* Date */}
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">
                      {format(booking.startTime, 'MMM d, yyyy')}
                    </h3>
                    
                    {/* Time Range */}
                    <p className="text-gray-600 mb-3">
                      {format(booking.startTime, 'h:mm a')} - {format(booking.endTime, 'h:mm a')}
                    </p>
                    
                    {/* Duration */}
                    <p className="text-gray-600 text-sm mb-4">
                      Duration: {booking.duration} minutes
                    </p>

                    {/* Clickable Participant Profile */}
                    <div 
                      className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => {
                        const participantId = booking.userRole === 'customer' ? booking.starId : booking.customerId;
                        handleProfileClick(participantId);
                      }}
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        {booking.participantProfile?.imageUrl ? (
                          <img
                            src={booking.participantProfile.imageUrl}
                            alt={booking.participantProfile.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-purple-100">
                            <span className="text-purple-600 text-sm font-medium">
                              {(booking.participantProfile?.name || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">
                          {booking.participantProfile?.name || 'Unknown'}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {booking.userRole === 'customer' ? 'Star' : 'Customer'}
                        </p>
                      </div>
                      {/* Click indicator */}
                      <div className="text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Join Call Button - Only show for upcoming calls */}
                    {booking.status === 'locked' && booking.startTime > new Date() && (
                      booking.roomUrl ? (
                        <button
                          onClick={() => {
                            const now = new Date();
                            const msUntilStart = booking.startTime.getTime() - now.getTime();
                            const minutesUntilStart = Math.ceil(msUntilStart / 60000);
                            if (minutesUntilStart <= 1) {
                              window.open(booking.roomUrl, '_blank', 'noopener,noreferrer');
                            } else {
                              setEarlyJoinModal({ show: true, minutesLeft: minutesUntilStart });
                            }
                          }}
                          className="inline-block bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors w-full sm:w-auto text-center"
                        >
                          Join Call
                        </button>
                      ) : (
                        <div className="text-gray-500 text-sm">
                          Call room will be ready 1 hour before start time
                        </div>
                      )
                    )}
                  </div>

                  {/* Price/Status */}
                  <div className="text-left sm:text-right">
                    {booking.status === 'open' ? (
                      <span className="text-orange-600 font-semibold">
                        Current Bid: ${Number(booking.currentBid).toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-green-600 font-semibold">
                        Booked: ${Number(booking.price).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Early Join Modal */}
      {earlyJoinModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setEarlyJoinModal({ show: false, minutesLeft: 0 })}>
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Not Started Yet</h3>
            <p className="text-gray-600 mb-4">
              Your meeting will start in {earlyJoinModal.minutesLeft} minute{earlyJoinModal.minutesLeft !== 1 ? 's' : ''}. You can join when it's time.
            </p>
            <button
              onClick={() => setEarlyJoinModal({ show: false, minutesLeft: 0 })}
              className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookingsScreen;